import { FC, useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { format, parse } from "date-fns";

import { AuthContext } from "src/context/authContext";
import { PatientFilterStateActions, pending_therapy } from "src/pages/patient/types";
import { PATIENT_PENDING_TREATMENT_HEADER_LISTING, PATIENT_PENDING_TREATMENT_MANUFACTURE_HEADER_LISTING } from "src/constants/patients";
import { PendingTherapyPatientProps } from "../types";
import AddNote from "src/components/modals/addNote";
import ReRunPatientModal from "src/components/modals/rerunPatientModal";
import ConfirmIndication from "src/components/modals/confirmIndication";
import { handleNavigation, isNotKAMUser } from "src/helpers";
import { ToastContext } from "src/context/toastContext";
import { LoaderContext } from "src/context/loaderContext";
import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";
import { ToastTypes } from "src/types";
import RequestDateOfTreatment from "src/components/modals/requestDateOfTreatment";
import CancelTreatment from "src/components/modals/cancelTreatment";
import { DEFAULT_REF_PAGE_SELECTION } from "src/constants";
import PracticeContactInfo from "src/components/practiceContactInfo";
import { ColumnSortProps } from "src/components/tableHeader/types";
import TableHeader from "src/components/tableHeader";
import { sortTableColumns } from "src/helpers/stats";


const PendingTherapyPatient: FC<PendingTherapyPatientProps> = ({ dataList, totalPages, currentPage, changePageNumber ,refreshListing}): JSX.Element => {
  const { isManufacturer, defaultView, userData } = useContext(AuthContext);
  const location = useLocation();
  // const [rowindex, setIndex] = useState<number>();
  const [pageSearch, setPageSearch] = useState(currentPage.toString());
  const [pendingDataList, updatePendingDataList] = useState<pending_therapy[]>([]);
  const [sortingConfig, setSortingConfig] = useState<ColumnSortProps>({
    sort_by: '',
    direction: 'desc',
  });

  const [saveNoteModal, setDataforNoteModal] = useState<{ isModalVisible: boolean; caseId: number; }>({
    isModalVisible: false,
    caseId: 0,
  });
  // const [showRequestTreatmentModal, setDataForRequestModalTreatment] = useState<{ isModalVisible: boolean; caseId: number; prescriptionId: number, bentype: string }>({
  //   isModalVisible: false,
  //   caseId: 0,
  //   prescriptionId: 0,
  //   bentype: ""
  // });
  const [showCancelIndicationModal, setDataForCancelIndication] = useState<{ isModalVisible: boolean; caseId: number; prescriptionId: number }>({
    isModalVisible: false,
    caseId: 0,
    prescriptionId: 0
  });
  const [showReRunPatientModal, setShowReRunPatientModal] = useState<{ isModalVisible: boolean; caseId: number; prescriptionId: number, title: string, bentype: string; investigated_at?: string; }>({
    isModalVisible: false,
    caseId: 0,
    prescriptionId: 0,
    title: '',
    bentype: ""
  });
  const { showToast } = useContext(ToastContext);
  const { setLoader, hideLoader } = useContext(LoaderContext);
  
  const [cancelTreatmentModal, setDataForCancelModal] = useState<{ isModalVisible: boolean; caseId: number; prescriptionId: number }>({
    isModalVisible: false,
    caseId: 0,
    prescriptionId: 0
  });
  const [showRequestTreatmentModal, setDataForRequestModalTreatment] = useState<{ isModalVisible: boolean; caseId: number; prescriptionId: number, bentype: string ,investigated_at?: string}>({
    isModalVisible: false,
    caseId: 0,
    prescriptionId: 0,
    bentype: "",
    investigated_at: ""
  });
  
  const { data: transferRxData, mutate: transferRxByCaseId } =
    useFetch(APIEndpoints.TRANSFER_RX);

  useEffect(() => {
    if (transferRxData) {
      hideLoader();
      const { message } = transferRxData as unknown as { code: number; message: string; };
      showToast([message], ToastTypes.SUCCESS);
      refreshListing && refreshListing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferRxData]);

  const ConfirmAlert = (title: string, buttonName: string, caseId: number) => {
    const result = window.confirm(title);
    if (result) {
      setLoader()
      transferRxByCaseId({}, { case_id: caseId }, { rep_xfer: true })
           //refreshListing && refreshListing();

    }
  }

  const checkForPageChange = (page: { selected: number }): void => {
    changePageNumber({
      type: PatientFilterStateActions.CHANGE_PENDING_THERAPY_PAGE,
      payload: page.selected + 1,
    });
    setPageSearch((page.selected + 1).toString())

  };

  useEffect(() => {
    setSortingConfig({ direction: 'desc', sort_by: '' })
    updatePendingDataList(dataList);
  }, [dataList]);

  const redirectDecision = () => {
    if (isManufacturer) {
      refreshListing?.()
      return;
    }

    const isRedirectRequired = handleNavigation(defaultView, location.pathname);
    if (isRedirectRequired) {
      if (isNotKAMUser(userData?.ref_type ?? ""))
        navigate(defaultView.includes("/") ? defaultView : `/${defaultView}`, { replace: true })
      else {
        if (DEFAULT_REF_PAGE_SELECTION.find((dashboard) => dashboard.label.toLowerCase().includes("patient"))) {
          navigate(defaultView.includes("/") ? defaultView : `/${defaultView}`, { replace: true })
        } else {
          navigate(location.pathname.includes("patient") ? `${location.pathname}${location.search}` : "/patient_statistics", { replace: true })
        }
      }
    } else {
      navigate(0)
    }
  }

  const navigate = useNavigate();
  const navigateToModalAndPage = (item: pending_therapy) => {
    if (item.re_run_type === 'popup') {
      setShowReRunPatientModal({ caseId: item.bi_case_id, isModalVisible: true, prescriptionId: item.prescription_id, title: "Please update case status below", bentype: "med", investigated_at: item.bi_completed ? parse(item.bi_completed, "MM/dd/yyyy", new Date()).toISOString() :  undefined })
    } else {
      navigate(`/cases/${item ? item.bi_case_id : 0}/clone`)
    }
  }

  const handlePageSearch = () => {
    const pageNumber = Number(pageSearch);

    if (!pageNumber || pageNumber < 1 || pageNumber > totalPages) {
      return;
    }

    changePageNumber({
      type: PatientFilterStateActions.CHANGE_PENDING_THERAPY_PAGE,
      payload: pageNumber,
    });

    setPageSearch(pageNumber.toString());
  };

  const changeIdOrder = (value: string): void => {
    sortTableColumns(value, sortingConfig, setSortingConfig, pendingDataList, updatePendingDataList)
  };

  return (
    <div className="card">
      {/* <div className="card-header custom-bg-purple">
        <h3 className="text-white mb-0">
          Patients Pending Treatment
        </h3>
      </div> */}

      <div className="card-body p-0">
        <div className="custom-bg-light-purple p-2">
          {!isManufacturer ? 
          <p className="mb-0 text-dark fs-6">
            These are patients currently awaiting action. They are listed in the "Action Dashboard" section.<i className="fas fa-comment"></i> indicates cases with new notes.
          </p> 
          : 
          <p className="mb-0 text-dark fs-6">
            These are patients currently awaiting action. For Case Type, 'A' indicates an automatically rerun case, 'M' indicates a manually re-run case, and 'New' indicates a case for a new patient.
          </p>
          }
        </div>

        <div className="table-responsive overflow-auto" style={{ maxHeight: "500px" }}>
          <table className="table table-striped table-sm mb-0">
            <thead className="sticky-top" style={{ zIndex: 0 }}>
              <tr className="text-center align-items-center">
                {isManufacturer ?
                  (
                    PATIENT_PENDING_TREATMENT_MANUFACTURE_HEADER_LISTING
                      .filter((tableHeader) => tableHeader.value !== "reviewed_by_fam" || userData?.ref_type === "rep")
                      .map((tableHeader, index) => (
                      userData && !isNotKAMUser(userData.ref_type) ?
                        (tableHeader.label !== "FAM/Office Note" ?
                          <TableHeader
                            key={index} 
                            tableHeader={{ 
                              ...tableHeader, 
                              label: tableHeader.label === "Treatment Status" ? "Provide Treatment Status" : tableHeader.label,
                              width: tableHeader.width || ""
                            }} 
                            sortConfig={sortingConfig}
                            changeIdOrder={changeIdOrder}
                          /> 
                          : <></>
                        )
                        : 
                        <TableHeader
                          key={index} 
                          tableHeader={{ 
                            ...tableHeader,
                            width: tableHeader.width || ""
                          }} 
                          sortConfig={sortingConfig}
                          changeIdOrder={changeIdOrder}
                        />
                      )
                    )
                  ) : (
                    PATIENT_PENDING_TREATMENT_HEADER_LISTING.map((tableHeader, index) => (
                      <TableHeader
                        key={index} tableHeader={{ ...tableHeader, width: tableHeader.width.toString() || "" }} sortConfig={sortingConfig}
                        changeIdOrder={changeIdOrder}
                      />
                    ))
                  )
                }
              </tr>
            </thead>

            <tbody>
              {pendingDataList.length > 0 ?
                pendingDataList.map((item, index) => {
                  const isOffice = item.kam_fam_notes?.type.toLowerCase().replace(/note/g, '').trim() === "office"
                  const isFam = item.kam_fam_notes?.type.toLowerCase().replace(/note/g, '').trim() === "fam"
                  return (
                  <tr key={index} className="align-middle text-center">
                    {isManufacturer ?
                      (item.patient_name !== 'Redacted'
                        ?
                        (<td className="align-middle user-select-text">
                          <Link to={`/global_patients/${item.patient_id}/edit`}>
                            <span className="user-select-text">{item.patient_name}</span>
                          </Link>

                        </td>)
                        :
                        (
                          <td className="text-black align-middle user-select-text">
                            <span className="user-select-text">{item.patient_name}</span>
                          </td>
                        )) :
                      (<td className="align-middle user-select-text">
                        <Link to={`/global_patients/${item.patient_id}/edit`}>
                          <span className="user-select-text">{item.patient_name}</span>
                        </Link>

                      </td>)
                    }
                    {/* <td className="align-middle">
                      <Link to={`/global_patients/${item.patient_id}/edit`}>
                        {item.patient_id}
                      </Link>
                    </td> */}
                    {/* <td className="align-middle">
                      {isManufacturer ? 
                        (
                          <>
                            {item.indiction?.toLowerCase() === "other" ? (
                              <p className="text-danger mb-0">{item.bi_case_id}</p>
                            ) : (
                              <Link to={`/cases/${item.bi_case_id}`}>
                                  {item.message_icon && !isManufacturer && <i className="fas fa-comment me-1"></i>}{item.bi_case_id}
                              </Link>
                            )}
                          </>
                        ) : (
                          <Link to={`/cases/${item.bi_case_id}`}>
                              {item.message_icon && !isManufacturer && <i className="fas fa-comment me-1"></i>}{item.bi_case_id}
                          </Link>
                      )}
                    </td> */}
                    {/* <td className="text-primary">
                      {isManufacturer ?
                        item && item?.indication && item?.indication.toLowerCase() === 'other' ?
                          <p className="fw-bold text-danger mb-0">{item.bi_case_id}</p> :
                          <Link to={`/cases/${item.bi_case_id}`}>
                            {item.message_icon && !isManufacturer && <i className="fas fa-comment me-1"></i>}{item.bi_case_id}
                          </Link>
                        :
                        <Link to={`/cases/${item.bi_case_id}`}>
                          {item.message_icon && !isManufacturer && <i className="fas fa-comment me-1"></i>}{item.bi_case_id}
                        </Link>
                      }
                    </td> */}
                    {userData && isNotKAMUser(userData?.ref_type) ?
                      <td className="align-middle text-center user-select-text">
                        <Link to={`/global_patients/${item.patient_id}/edit`}>
                         <span className="user-select-text">{item.patient_id}</span> 
                        </Link>
                      </td> :
                      <td className="align-middle text-center text-black user-select-text">
                        {/* <Link to={`/global_patients/${item.patient_id}/edit`}> */}
                        <span className="user-select-text">{item.patient_id}</span>
                        {/* </Link> */}
                      </td>
                    }
                    <td className="align-middle text-center user-select-text">
                      {isManufacturer ? (
                        (userData && isNotKAMUser(userData?.ref_type))
                          ?
                          <>
                            {item.indication && item.indication.toLowerCase() === "other" ? (
                              <p className="fw-bold text-danger mb-0">{item.bi_case_id}</p>
                            ) : (
                              <Link to={`/cases/${item.bi_case_id}`}>
                                {item.message_icon && !isManufacturer && <i className="fas fa-comment me-1"></i>}{item.bi_case_id}
                              </Link>
                            )}
                          </> :
                          <>
                            {item.indication && item.indication.toLowerCase() === "other" ? (
                              <p className="fw-bold text-danger mb-0">{item.bi_case_id}</p>
                            ) : (
                              <p className="align-middle text-center text-black mb-0">
                                {item.message_icon && !isManufacturer && <i className="fas fa-comment me-1"></i>}{item.bi_case_id}
                              </p>
                            )}
                          </>
                      ) : (
                        <Link to={`/cases/${item.bi_case_id}`}>
                          {item.message_icon && !isManufacturer && <i className="fas fa-comment me-1"></i>}{item.bi_case_id}
                        </Link>
                      )}
                    </td>
                    {userData?.ref_type === "rep" &&
                      <td className="align-middle">
                        {item.reviewed_by_fam}
                      </td>
                    }
                    {isManufacturer && <td className="align-middle">
                      <span className={`btn btn-sm ${item.reverification.toLowerCase() === "new" ? "btn-green btn-new" : "btn-green btn-new"}`}>
                        {item.reverification}
                      </span>
                    </td>}
                    {/* <td className="text-center align-middle fs-6">
                    {isManufacturer ? (
                      typeof item.case_type === "string" && item.case_type ? (
                        <button type="button" className={`btn btn-sm ${item.case_type.toLowerCase() === "new" ? "btn-success" : "btn-green"}`}>
                          {item.case_type}
                        </button>
                      ) : null
                    ) : (
                      typeof item.reverification === "string" && item.reverification ? (
                        <button type="button" className={`btn btn-sm ${item.reverification.toLowerCase() === "new" ? "btn-success" : "btn-green"}`}>
                          {item.reverification}
                        </button>
                      ) : null
                    )}
                  </td> */}
                    <td className="align-middle">
                      {isManufacturer ? item.payer : item.insurance_type}
                    </td>
                    <td className="align-middle">
                      <PracticeContactInfo
                        name={item.practice_name} 
                        address={item.practice_full_address} 
                        phone={item.practice_phone_number ?? ""} 
                        fax={item.practice_fax_number ?? ""}
                        indexKey={index}
                      >
                        <>{item.provider}</>
                      </PracticeContactInfo>
                    </td>
                    {userData && isNotKAMUser(userData.ref_type) ? <td className={`align-middle text-light background-color-${isManufacturer ? item.treatment_status_color : item.color_idx}`}>
                      {item.treatment_status}
                    </td> : (
                      <td className={`text-center text-light align-middle`} id="next-steps">
                        {item.indication && item.indication.toLowerCase() === "other" ? <span className="text-black">No Action Available</span> : null}
                        {item && item.next_steps && item.indication && item.indication.toLowerCase() !== "other" && item.next_steps.length > 0 &&
                          item.next_steps.map((step, index) => {
                            if (step === "Treated") {
                              return (
                                <button key={index} className="btn btn-green btn-sm m-1 rounded-0"
                                  onClick={() => setDataForRequestModalTreatment({ isModalVisible: true, caseId: item.bi_case_id, prescriptionId: item.prescription_id, bentype: "med", investigated_at: item.bi_completed ? parse(item.bi_completed, "MM/dd/yyyy", new Date()).toISOString() :  "" })}
                                  type="button"
                                >
                                  <i className="fa-clipboard far"></i> Treated
                                </button>
                              );
                            } else if (step === "Not Treating") {
                              return (
                                <button key={index} className="btn btn-green btn-sm m-1 rounded-0"
                                  onClick={() => setDataForCancelModal({ isModalVisible: true, caseId: item.bi_case_id, prescriptionId: item.prescription_id })}
                                  type="button"
                                >
                                  <i className="fa-times fas"></i> Not Treated
                                </button>
                              );
                            } else if (step === "Not Treated") {
                              return (
                                <button key={index} className="btn btn-green btn-sm m-1 rounded-0"
                                  onClick={() => setDataForCancelModal({ isModalVisible: true, caseId: item.bi_case_id, prescriptionId: item.prescription_id })}
                                  type="button"
                                >
                                  <i className="fa-times fas"></i> Not Treated
                                </button>
                              );
                            } else if (step === "Transferred") {
                              return (
                                <button key={index} className="btn btn-green btn-sm m-1 rounded-0"
                                  onClick={() => ConfirmAlert('By clicking this you are noting the office has tranferred this prescription to a Specialty Pharmacy. Are you sure you want to mark this prescription transferred? This cannot be undone!', "xFerToSP", item.bi_case_id)}
                                  type="button"
                                >
                                  <i className="fa-prescription fas"></i> Transferred
                                </button>
                              );
                            } else if (step === "Treatment Scheduled") {
                              return (
                                <button key={index} className="btn btn-green btn-sm m-1 rounded-0 background-color-3"
                                  onClick={() => setDataForRequestModalTreatment({ isModalVisible: true, caseId: item.bi_case_id, prescriptionId: item.prescription_id, bentype: "sd" })}
                                  type="button"
                                >
                                  <i className="fa-times fas"></i> Treatment Scheduled
                                </button>
                              );
                            } else if (step.includes("Scheduled Tx Date")) {
                              return (
                                <button key={index} className="btn btn-link user-select-text "
                                  onClick={() => setDataForRequestModalTreatment({ isModalVisible: true, caseId: item.bi_case_id, prescriptionId: item.prescription_id, bentype: "sd" })}
                                  type="button">
                                  {step}
                                </button>
                              );
                            } else {
                              return (<span key={index} className="text-black">{step}</span>);
                            }
                          })
                        }
                      </td>
                    )}
                    <td className="align-middle">
                      {item.bi_submitted ? format(item.bi_submitted, "MM/dd/yyyy") : 'N/A'}
                    </td>
                    <td className="align-middle">
                      {item.bi_completed ? format(item.bi_completed, "MM/dd/yyyy") : 'N/A'}
                    </td>
                    {isManufacturer ? (
                      <>
                        <td className="align-middle">
                          {item.indication ? item.indication : 'N/A'}
                        </td>
                        {/* <td className="align-middle">
                          {item.confirm_indication && item.confirm_indication.clickable ?
                              <button
                                type="button" className="btn btn-success btn-sm rounded-0"
                                onClick={() => setDataForCancelIndication({ isModalVisible: true, caseId: item.bi_case_id, prescriptionId: item.prescription_id })}
                              >
                                Confirm Indication
                              </button>
                              : <p className="mb-0">{item.confirm_indication.text}</p>
                          }
                        </td> */}
                        {userData && isNotKAMUser(userData.ref_type) && <td className="align-middle">
                          {item?.kam_fam_notes ? (
                            item?.kam_fam_notes.text === 'Add Note' ? (
                              <button type="button" className="btn btn-green btn-sm rounded-0" onClick={() => { setDataforNoteModal({ isModalVisible: true, caseId: item.bi_case_id }) }} >
                                <i className="fas fa-times"></i>{' '}{item?.kam_fam_notes.text}
                              </button>
                            ) : (
                              <button
                                className={`btn btn-link ${isOffice ? "btn-office" : isFam ? "btn-fam" : ""} user-select-text`}
                                onClick={() =>
                                  setDataforNoteModal({ isModalVisible: true, caseId: item.bi_case_id })
                                }
                              >
                                {item?.kam_fam_notes.text}
                              </button>
                              
                            )
                          ) : 'N/A'}
                        </td>}
                      </>
                    ) : (
                      (!isManufacturer ? (
                        <>
                        <td>
                          <span className="btn btn-green text-nowrap rounded-0" onClick={() => navigateToModalAndPage(item)}>
                            <i className="fas fa-sync-alt"></i> Re-run
                          </span>
                        </td>
                        <td className="align-middle text-center fs-6" style={{ minWidth: '105px' }}>
                        {item?.kam_fam_notes?.clickable && item?.kam_fam_notes.text ? (
                          item?.kam_fam_notes.text === 'Add Note' ? (
                            <button
                              type="button"
                              className="btn btn-link btn-green btn-sm rounded-0 user-select-text"
                              onClick={() =>
                                setDataforNoteModal({ isModalVisible: true, caseId: item.bi_case_id })
                              }
                            >
                              <i className="fas fa-times me-1"></i>
                              {item?.kam_fam_notes.text}
                            </button>
                            
                          ) : item.kam_fam_notes.text === 'Confirm Indication' ? (
                            <button
                              type="button"
                              className="btn btn-green btn-sm rounded-0"
                              onClick={() =>
                                setDataforNoteModal({ isModalVisible: true, caseId: item.bi_case_id })
                              }
                            >
                              <i className="fas fa-times me-1"></i>
                              {"Add Note"}
                            </button>
                          ) : 
                          // !(String(item?.kam_fam_notes?.text ?? "").includes("No FAM Action Needed") &&  !isManufacturer) ? (
                          //   <button
                          //     className={`btn btn-link ${isOffice ? "btn-office" : isFam ? "btn-fam" : ""} user-select-text`}
                          //     onClick={() =>
                          //       setDataforNoteModal({ isModalVisible: true, caseId: item.bi_case_id })
                          //     }
                          //   >
                          //     {item?.kam_fam_notes.text}
                          //   </button>
                          // ) : ( 
                          // <button
                          //     type="button"
                          //     className="btn btn-green btn-sm rounded-0"
                          //     onClick={() =>
                          //       setDataforNoteModal({ isModalVisible: true, caseId: item.bi_case_id })
                          //     }
                          //   >
                          //     <i className="fas fa-times me-1"></i>
                          //     {"Add Note"}
                          //   </button>)
                          (isManufacturer || !String(item.kam_fam_notes?.text).includes("No FAM Action Needed"))
                                  ? (
                                    <button
                                      className={`btn btn-link ${isOffice ? "btn-office" : isFam ? "btn-fam" : ""} user-select-text`}
                                      onClick={() =>
                                        setDataforNoteModal({
                                          isModalVisible: true,
                                          caseId: item.bi_case_id
                                        })
                                      }
                                    >
                                      {item.kam_fam_notes?.text}
                                    </button>
                                    
                                  ) : (
                                    <button
                                      type="button"
                                      className="btn btn-green btn-sm rounded-0"
                                      onClick={() =>
                                        setDataforNoteModal({
                                          isModalVisible: true,
                                          caseId: item.bi_case_id
                                        })
                                      }
                                    >
                                      <i className="fas fa-times me-1"></i> Add Note
                                    </button>
                                  )
                        ) : (
                         <button
                              type="button"
                              className="btn btn-green btn-sm rounded-0"
                              onClick={() =>
                                setDataforNoteModal({ isModalVisible: true, caseId: item.bi_case_id })
                              }
                            >
                              <i className="fas fa-times me-1"></i>
                              {"Add Note"}
                            </button>
                        )}
                      </td>
                      </>

                      ) : null)
                    )}
                  </tr>
                  )
                })
                : null}
            </tbody>
          </table>
        </div>

        {totalPages && totalPages > 1 ? (
          <div className="pagination pagination-sm pagination-lg justify-content-center flex-wrap mt-3">
            {currentPage !== 1 ? (
              <li className="page-item">
                <button className="btn btn-link page-link rounded-0" onClick={() => checkForPageChange({ selected: 0 })}>
                  <i className="bi bi-chevron-double-left"></i> First
                </button>
              </li>
            ) : null}

            <ReactPaginate
              nextLabel={<>Next <i className="bi bi-chevron-right"></i></>}
              onPageChange={checkForPageChange}
              pageRangeDisplayed={3}
              marginPagesDisplayed={2}
              pageCount={totalPages}
              previousLabel={<><i className="bi bi-chevron-left"></i> Previous</>}
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link rounded-0"
              nextClassName="page-item"
              nextLinkClassName="page-link rounded-0"
              breakLabel="..."
              breakClassName="page-item"
              breakLinkClassName="page-link"
              containerClassName="pagination pagination-sm pagination-lg"
              activeClassName="active"
              renderOnZeroPageCount={null}
              forcePage={currentPage - 1}
            />

            {currentPage !== totalPages ? (
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="btn btn-link page-link rounded-0" onClick={() => checkForPageChange({ selected: totalPages - 1 })}>
                  Last <i className="bi bi-chevron-double-right"></i>
                </button>
              </li>
            ) : null}
            <li className="page-item">
              <input
                type="number"
                min={1}
                max={totalPages}
                className="form-control page-link"
                style={{ width: "67px" }}
                placeholder="Page"
                value={pageSearch}
                onChange={(e) => setPageSearch(e.target.value)}
                onBlur={handlePageSearch}          // 👈 Fire on focus out
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.currentTarget.blur();        // 👈 Trigger blur = auto jump
                  }
                }}
              />
            </li>
          </div>
        ) : null}
      </div>
      <AddNote
        isModalVisible={saveNoteModal.isModalVisible}
        caseId={saveNoteModal.caseId}
        refreshListing={() => isManufacturer ? refreshListing?.() : redirectDecision()}
        hideModal={() => {
          setDataforNoteModal({
            isModalVisible: false,
            caseId: 0,
          });
        }}
      />
      <ReRunPatientModal
        isModalVisible={showReRunPatientModal.isModalVisible}
        caseId={showReRunPatientModal.caseId}
        prescriptionId={showReRunPatientModal.prescriptionId}
        bentype={showReRunPatientModal.bentype}
        title={showReRunPatientModal.title}
        investigated_at={showReRunPatientModal.investigated_at}
        hideModal={() => {
          setShowReRunPatientModal({
            isModalVisible: false,
            caseId: 0,
            prescriptionId: 0,
            bentype: '',
            title: "",
            investigated_at: undefined
          });
        }}
        refreshListing={() => redirectDecision()}
      />

      <ConfirmIndication
        isModalVisible={showCancelIndicationModal.isModalVisible}
        caseId={showCancelIndicationModal.caseId}
        prescriptionId={showCancelIndicationModal.prescriptionId}
        hideModal={() => {
          setDataForCancelIndication({
            isModalVisible: false,
            caseId: 0,
            prescriptionId: 0
          });
        }}
        refreshListing={() => redirectDecision()}
      />
       {showRequestTreatmentModal.isModalVisible && (
              <RequestDateOfTreatment
                isModalVisible={showRequestTreatmentModal.isModalVisible}
                caseId={showRequestTreatmentModal.caseId}
                prescriptionId={showRequestTreatmentModal.prescriptionId}
                bentype={showRequestTreatmentModal.bentype}
                investigated_at={showRequestTreatmentModal.investigated_at}
                hideModal={() => {
                  setDataForRequestModalTreatment({
                    isModalVisible: false,
                    caseId: 0,
                    prescriptionId: 0,
                    bentype: '',
                    investigated_at: ""
                  });
                }}
                refreshListing={() => redirectDecision()}
              />
            )}
      
            <CancelTreatment
              isModalVisible={cancelTreatmentModal.isModalVisible}
              caseId={cancelTreatmentModal.caseId}
              prescriptionId={cancelTreatmentModal.prescriptionId}
              hideModal={() => {
                setDataForCancelModal({
                  isModalVisible: false,
                  caseId: 0,
                  prescriptionId: 0
                });
              }}
              refreshListing={() => redirectDecision()}
            />
    </div>
  );
};

export default PendingTherapyPatient;
